import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { DATA_INJECTION_FUNCTION_NAMES, IFRAME_SCRIPT_READY_DELAY } from '../utils';

interface UseIframeDataInjectorParams {
  htmlContent: string;
  jsonData: unknown;
}

interface UseIframeDataInjectorResult {
  iframeRef: MutableRefObject<HTMLIFrameElement | null>;
  handleIframeLoad: () => void;
}

const IFRAME_DATA_INJECTION_MAX_RETRY_COUNT = 10;

export default function useIframeDataInjector(props: UseIframeDataInjectorParams): UseIframeDataInjectorResult {
  const { htmlContent, jsonData } = props;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [injectTrigger, setInjectTrigger] = useState(0);

  const handleIframeLoad = (): void => {
    setIframeLoaded(true);
    setInjectTrigger((value) => value + 1);
  };

  useEffect(() => {
    setIframeLoaded(false);
    setInjectTrigger((value) => value + 1);
  }, [htmlContent]);

  useEffect(() => {
    if (typeof jsonData === 'undefined' || !iframeLoaded || !htmlContent || !iframeRef.current) {
      return;
    }

    let retryCount = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const injectDataToIframe = () => {
      const contentWindow = iframeRef.current?.contentWindow;

      if (!contentWindow) {
        if (retryCount >= IFRAME_DATA_INJECTION_MAX_RETRY_COUNT) {
          console.error('[LowCodeTemplateWithDataJSON] iframe contentWindow not accessible');
          return;
        }

        retryCount += 1;
        timer = setTimeout(injectDataToIframe, IFRAME_SCRIPT_READY_DELAY);
        return;
      }

      try {
        let setDataFn: Function | null = null;

        DATA_INJECTION_FUNCTION_NAMES.some((fnName) => {
          const fn = (contentWindow as any)[fnName];

          if (typeof fn === 'function') {
            setDataFn = fn;
            return true;
          }

          return false;
        });

        if (setDataFn) {
          setDataFn(jsonData);
          return;
        }

        if (retryCount >= IFRAME_DATA_INJECTION_MAX_RETRY_COUNT) {
          console.error('[LowCodeTemplateWithDataJSON] Data injection function not found');
          console.error(`Looking for: ${DATA_INJECTION_FUNCTION_NAMES.join(', ')}`);
          try {
            console.error('Available window keys:', Object.keys(contentWindow));
          } catch (enumerateError) {
            console.error('Cannot enumerate iframe window properties:', enumerateError);
          }
          return;
        }

        retryCount += 1;
        timer = setTimeout(injectDataToIframe, IFRAME_SCRIPT_READY_DELAY);
      } catch (error) {
        console.error('[LowCodeTemplateWithDataJSON] Error accessing iframe:', error);
      }
    };

    timer = setTimeout(injectDataToIframe, IFRAME_SCRIPT_READY_DELAY);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [jsonData, iframeLoaded, htmlContent, injectTrigger]);

  return {
    iframeRef,
    handleIframeLoad,
  };
}

export type { UseIframeDataInjectorParams, UseIframeDataInjectorResult };
